import { CircularProgressbar } from 'react-circular-progressbar';
import { IUser } from 'src/utils/interfaces/user';
import Spinner from '../spinner';
import { useDialog } from 'src/customHooks/useDialog';
import { useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import 'react-circular-progressbar/dist/styles.css';
import './modal.scss';

interface IModalProps {
    titleContent: React.ReactNode;
    bodyContent: string;
    footerContent?: any;
    open: boolean;
    cancelBtnLabel: string;
    confirmBtnLabel: string;
    onCancelHandler: Function;
    onConfirmHandler: Function;
    isLoading?: boolean;
    isConfirmBtnRed?: boolean;
    isContentCentered?: boolean;
    showButtons?: boolean;
    icon?: string;
    isBigModal?: boolean;
    isError?: boolean;
    progress?: number;
    modalExtraClass?: string;
    secondBodyContent?: string;
    showConfirmButton?: boolean;
    secondCancelBtnLabel?: string;
    thirdCancelBtnLabel?: string;
    onSecondCancelHandler?: Function;
    onThirdCancelHandler?: Function;
    /** When true, the dialog stays open after confirm click so the consumer
     *  can show a loader and close the modal manually via the open prop. */
    keepOpenOnConfirm?: boolean;
}

const DialogModal: React.FC<IModalProps> = ({
    titleContent,
    bodyContent,
    footerContent,
    open,
    cancelBtnLabel,
    confirmBtnLabel,
    onCancelHandler,
    onConfirmHandler,
    isLoading,
    isConfirmBtnRed,
    isContentCentered = false,
    showButtons = true,
    icon,
    isBigModal = false,
    isError = false,
    progress = null,
    modalExtraClass = '',
    secondBodyContent = '',
    showConfirmButton = true,
    secondCancelBtnLabel = undefined,
    thirdCancelBtnLabel = undefined,
    onSecondCancelHandler = undefined,
    onThirdCancelHandler = undefined,
    keepOpenOnConfirm = false,
}) => {
    const brand: string = useSelector((state: IUser) => {
        const stateBrand = state.dealerDataInfo?.dealer.brand || 'vw';
        return stateBrand.toLowerCase();
    });
    const CERO = 0;
    const { dialogRef, closeDialogRef, openDialogRef } = useDialog();

    useEffect(() => {
        if (open) {
            openDialogRef();
        } else {
            closeDialogRef();
        }
    }, [open]);

    const cancelHandler = async () => {
        if (onCancelHandler) {
            await closeDialogRef();
            onCancelHandler();
        }
    };

    const secondCancelHandler = async () => {
        if (onSecondCancelHandler) {
            await closeDialogRef();
            onSecondCancelHandler();
        }
    };

    const thirdCancelHandler = async () => {
        if (onThirdCancelHandler) {
            await closeDialogRef();
            onThirdCancelHandler();
        }
    };

    const confirmHanlder = async () => {
        if (onConfirmHandler) {
            if (!keepOpenOnConfirm) {
                await closeDialogRef();
            }
            onConfirmHandler();
        }
    };

    return (
        <dialog
            ref={dialogRef}
            className={`c-modal ${modalExtraClass}`}
            aria-labelledby='c-modal'
        >
            <div
                className={`c-modal__wrapper dialog-modal__container${
                    isBigModal ? ' xl' : ''
                }`}
                role='dialog'
                aria-labelledby='foo-bar-baz'
            >
                <div className='c-modal__header' id='dialog'>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent:
                                isContentCentered ||
                                icon ||
                                progress ||
                                progress == CERO
                                    ? 'center'
                                    : 'flex-start',
                            textAlign:
                                isContentCentered ||
                                icon ||
                                progress ||
                                progress == CERO
                                    ? 'center'
                                    : 'left',
                            alignContent: 'center',
                            alignItems:
                                isContentCentered ||
                                icon ||
                                progress ||
                                progress == CERO
                                    ? 'center'
                                    : 'flex-start',
                            width: '100%',
                            flexDirection: 'column',
                        }}
                    >
                        {icon && (
                            <div className='icon-conatiner'>
                                <i
                                    className={`c-icon c-icon--xxlarge c-icon--${icon}${
                                        isError ? ' error-color' : ''
                                    }`}
                                    role='img'
                                    aria-hidden='true'
                                />
                            </div>
                        )}
                        {(progress || progress == CERO) && (
                            <div className='loader-conatiner'>
                                <CircularProgressbar
                                    value={progress}
                                    text={`${progress}%`}
                                    className='loader-image'
                                    strokeWidth={5}
                                    styles={{
                                        path: {
                                            stroke: '#001E50',
                                            strokeLinecap: 'butt',
                                            transition:
                                                progress === CERO
                                                    ? 'none'
                                                    : 'stroke-dashoffset 0.9s ease 0s',
                                        },
                                        text: {
                                            fill: '#000000',
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            </div>
                        )}
                        <div
                            className={`c-modal__title__wrapper${
                                isContentCentered ? ' centered' : ''
                            }`}
                        >
                            <h2
                                className={`c-modal__title${
                                    isError ? ' error-color' : ''
                                }${isBigModal ? ' extra-spacing' : ''}`}
                                id='foo-bar-baz'
                            >
                                {titleContent}
                            </h2>
                        </div>
                    </div>
                    {showButtons && (
                        <button
                            className='c-modal__close-btn  js-modal-close'
                            type='button'
                            aria-label='Close this dialog window.'
                            onClick={cancelHandler}
                        ></button>
                    )}
                </div>

                <div className='c-modal__content' role='document'>
                    <div
                        className={`dialog-modal${
                            isContentCentered ? ' centered' : ''
                        }${showButtons == false ? ' extra-bottom' : ''}`}
                    >
                        <p
                            dangerouslySetInnerHTML={{
                                __html: bodyContent,
                            }}
                        ></p>
                        <p
                            className='secondaryContent'
                            dangerouslySetInnerHTML={{
                                __html: secondBodyContent,
                            }}
                        ></p>
                        {showButtons && (
                            <div
                                className={`u-mt-large u-pb-small button-group${
                                    isContentCentered ? ' centered' : ''
                                }${isBigModal ? ' extra-spacing' : ''}${
                                    secondCancelBtnLabel ? ' space-evenly' : ''
                                }`}
                            >
                                {cancelBtnLabel && (
                                    <button
                                        className={`c-btn radius-${brand} c-btn--secondary${
                                            isBigModal && !secondCancelBtnLabel
                                                ? ' xl-button'
                                                : ''
                                        }`}
                                        onClick={cancelHandler}
                                    >
                                        <span className='c-btn__text'>
                                            {cancelBtnLabel}
                                        </span>
                                    </button>
                                )}
                                {secondCancelBtnLabel && (
                                    <button
                                        className={`c-btn radius-${brand} c-btn--secondary fixed-width`}
                                        onClick={secondCancelHandler}
                                    >
                                        <span className='c-btn__text'>
                                            {secondCancelBtnLabel}
                                        </span>
                                    </button>
                                )}
                                {thirdCancelBtnLabel && (
                                    <button
                                        className={`c-btn radius-${brand} c-btn--secondary fixed-width`}
                                        onClick={thirdCancelHandler}
                                    >
                                        <span className='c-btn__text'>
                                            {thirdCancelBtnLabel}
                                        </span>
                                    </button>
                                )}
                                {showConfirmButton && (
                                    <button
                                        className={
                                            isLoading
                                                ? 'c-btn'
                                                : `c-btn bg-${brand} ${
                                                      isBigModal
                                                          ? ' xl-button'
                                                          : ''
                                                  }`
                                        }
                                        onClick={confirmHanlder}
                                        disabled={isLoading}
                                        style={
                                            isConfirmBtnRed
                                                ? { background: '#E4002C' }
                                                : {}
                                        }
                                    >
                                        <span className='c-btn__text'>
                                            {isLoading ? (
                                                <Spinner />
                                            ) : (
                                                confirmBtnLabel
                                            )}
                                        </span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {footerContent ? (
                    <div className='c-modal__footer'>{footerContent}</div>
                ) : null}
            </div>
        </dialog>
    );
};

export default DialogModal;
